import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu, X, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import InstallButton from "@/components/InstallButton";
import { ReadAloud } from "@/components/ReadAloud";
import { Logo, Wordmark } from "@/components/Logo";

function getPageText() {
  const main = document.querySelector("main");
  if (!main) return "";
  return (main.innerText || "").replace(/\s+\n/g, "\n").trim();
}

const primaryLinks = [
  { to: "/study-path", label: "Study Path" },
  { to: "/map", label: "Rabbit Hole" },
  { to: "/courses", label: "Courses" },
  { to: "/flashcards", label: "Flashcards" },
  { to: "/blacks", label: "Black's Law" },
  { to: "/caselaw", label: "Case Law" },
  { to: "/community", label: "Community" },
  { to: "/assistant", label: "AI Assistant" },
];
const moreLinks = [
  { to: "/dictionary", label: "Dictionary" },
  { to: "/teachings", label: "Teachings" },
  { to: "/statutes", label: "Statutes" },
  { to: "/states", label: "State Law" },
  { to: "/research", label: "Research" },
  { to: "/my-paths", label: "My Paths" },
  { to: "/templates", label: "Templates" },
  { to: "/credits", label: "AI Credits" },
  { to: "/support", label: "Support" },
];
const links = [...primaryLinks, ...moreLinks];

// Grouped navigation for the mobile menu — keeps a long list organized.
const MENU_GROUPS = [
  { heading: "Study & Research", items: [
    { to: "/study-path", label: "Study Path" }, { to: "/map", label: "Rabbit Hole" },
    { to: "/courses", label: "Courses" }, { to: "/flashcards", label: "Flashcards" },
    { to: "/blacks", label: "Black's Law" }, { to: "/caselaw", label: "Case Law" },
    { to: "/dictionary", label: "Dictionary" }, { to: "/statutes", label: "Statutes" },
    { to: "/states", label: "State Law" }, { to: "/teachings", label: "Teachings" },
    { to: "/research", label: "Research" }, { to: "/my-paths", label: "My Paths" },
    { to: "/templates", label: "Templates" },
  ]},
  { heading: "Community & Legal Assistance", items: [
    { to: "/community", label: "Community" }, { to: "/assistant", label: "AI Assistant" },
    { to: "/credits", label: "AI Credits" },
  ]},
  { heading: "Support & Account", items: [
    { to: "/support", label: "Support" },
  ]},
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const gated = user && !(user.subscription_active || user.role === "admin");
  const moreActive = moreLinks.some((l) => location.pathname.startsWith(l.to));

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 group shrink-0" data-testid="nav-logo">
          <Logo className="w-7 h-7" />
          <Wordmark className="font-serif text-lg font-semibold tracking-tight whitespace-nowrap" />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 flex-1 justify-center">
          {(gated ? [] : primaryLinks).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm transition-colors hover:text-primary whitespace-nowrap ${
                location.pathname.startsWith(l.to) ? "text-primary" : "text-muted-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {!gated && user && (
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen((v) => !v)}
                data-testid="nav-more"
                className={`flex items-center gap-1 text-sm transition-colors hover:text-primary whitespace-nowrap ${moreActive || moreOpen ? "text-primary" : "text-muted-foreground"}`}
              >
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-8 z-50 bg-card border border-border rounded-sm shadow-xl min-w-[180px] py-1.5" data-testid="nav-more-menu">
                  {moreLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`block px-4 py-2 text-sm transition-colors hover:bg-muted hover:text-primary ${location.pathname.startsWith(l.to) ? "text-primary" : "text-foreground/80"}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className={`text-sm transition-colors hover:text-primary whitespace-nowrap ${location.pathname.startsWith("/admin") ? "text-primary" : "text-muted-foreground"}`}>
              Admin
            </Link>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <ReadAloud label="Play page" getText={getPageText} className="border border-border rounded-sm px-2.5 py-1.5 hover:border-primary" />
              <InstallButton />
              <span className="text-sm text-muted-foreground font-mono">{user.name}</span>
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="text-sm bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:-translate-y-[1px] transition-transform"
              >
                Enroll Now
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-card px-6 py-5 flex flex-col gap-6 max-h-[80vh] overflow-y-auto">
          {(() => {
            const canSeeAll = !user || user.subscription_active || user.role === "admin";
            return MENU_GROUPS.map((group, gi) => {
              // Gate Study/Community groups behind membership; Support group is always available.
              if (gi < 2 && !canSeeAll) return null;
              const isAdmin = user && user.role === "admin";
              return (
                <div key={group.heading} className="flex flex-col gap-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary/80">{group.heading}</span>
                  {group.items.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                      className={`text-sm ${location.pathname === l.to ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      {l.label}
                    </Link>
                  ))}
                  {gi === 2 && isAdmin && (
                    <>
                      <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-mobile" className={`text-sm ${location.pathname === "/admin" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>Admin</Link>
                      <Link to="/admin/reconciliation" onClick={() => setOpen(false)} data-testid="nav-reconciliation-mobile" className={`text-sm ${location.pathname.startsWith("/admin/reconciliation") ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>Reconciliation</Link>
                      <Link to="/admin/support" onClick={() => setOpen(false)} data-testid="nav-admin-support-mobile" className={`text-sm ${location.pathname.startsWith("/admin/support") ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>Support Inbox</Link>
                    </>
                  )}
                  {gi === 2 && (
                    user ? (
                      <button onClick={handleLogout} className="text-left text-sm text-destructive">Sign out</button>
                    ) : (
                      <>
                        <Link to="/login" onClick={() => setOpen(false)} className="text-sm">Sign in</Link>
                        <Link to="/register" onClick={() => setOpen(false)} className="text-sm text-primary">Enroll Now</Link>
                      </>
                    )
                  )}
                </div>
              );
            });
          })()}
          <div className="pt-2 flex flex-col gap-3 border-t border-border">
            <ReadAloud label="Play this page" getText={getPageText} />
            <InstallButton label="Install app" />
          </div>
        </div>
      )}
    </header>
  );
}
